/**
 * Caixa de validação — o ponto de portaria que este aparelho representa
 * ("Portaria Principal", "Entrada VIP", "Camarote").
 *
 * Igual ao TMobi original: caixa do tipo validação é local, não abre dinheiro e
 * não sobe para o servidor. Serve para o operador saber em que posto está e
 * para separar as contagens deste aparelho.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import {ticketeira} from './config';

const CHAVE_CAIXA = 'tmobi.validador.caixa';

/**
 * Cópia em memória do último estado lido. Existe porque a navegação precisa
 * decidir na hora — sem esperar promessa — se ainda há caixa aberto.
 */
let emMemoria: Caixa | null = null;

export function caixaEmCache(): Caixa | null {
  return emMemoria;
}

export interface Caixa {
  nome: string;
  abertoEm: string;
  /** Operador que abriu — se trocar de usuário, o caixa é fechado junto. */
  usuarioId: string;
  ticketeira: string;
  /**
   * Evento que este caixa está validando. Fica gravado na primeira escolha e
   * não muda enquanto o caixa estiver aberto: a validação local é vinculada ao
   * caixa, então trocar de evento no meio misturaria contagem e fila de envio.
   */
  eventoId?: number;
  eventoNome?: string;
  /**
   * Vira true quando a portaria começa (primeira ida ao validador). Daí em
   * diante mexer nos setores exige liberação de quem tem acesso: mudar setor no
   * meio do evento muda quem entra e quem não entra.
   */
  setoresTravados?: boolean;
  /**
   * Modo mãos livres ligado neste caixa.
   *
   * Fica gravado aqui, e não em estado de tela, porque a escolha é do turno e
   * não da sessão do app: se o aparelho reiniciar no meio do evento, a portaria
   * volta lendo sem toque em vez de perguntar de novo com a fila na frente.
   */
  maosLivres?: boolean;
}

export async function abrirCaixa(nome: string, usuarioId: string): Promise<Caixa> {
  const caixa: Caixa = {
    nome: nome.trim(),
    abertoEm: new Date().toISOString(),
    usuarioId,
    ticketeira: ticketeira().id,
  };
  await AsyncStorage.setItem(CHAVE_CAIXA, JSON.stringify(caixa));
  emMemoria = caixa;
  return caixa;
}

/**
 * Caixa aberto neste aparelho, se houver. Caixa de outro operador ou de outra
 * ticketeira é descartado — o posto pertence a quem abriu.
 */
export async function caixaAberto(usuarioId?: string): Promise<Caixa | null> {
  const bruto = await AsyncStorage.getItem(CHAVE_CAIXA);
  if (!bruto) {
    emMemoria = null;
    return null;
  }
  try {
    const caixa = JSON.parse(bruto) as Caixa;
    const outraTicketeira = caixa.ticketeira !== ticketeira().id;
    const outroOperador = usuarioId !== undefined && caixa.usuarioId !== usuarioId;

    if (outraTicketeira || outroOperador) {
      await fecharCaixa();
      return null;
    }
    emMemoria = caixa;
    return caixa;
  } catch {
    emMemoria = null;
    return null;
  }
}

/**
 * Amarra o caixa ao evento escolhido. Só tem efeito na primeira escolha — para
 * trocar de evento é preciso fechar o caixa.
 */
export async function vincularEvento(
  eventoId: number,
  eventoNome: string,
): Promise<Caixa | null> {
  const caixa = await caixaAberto();
  if (!caixa) {
    return null;
  }
  if (caixa.eventoId !== undefined) {
    return caixa;
  }

  const vinculado: Caixa = {...caixa, eventoId, eventoNome};
  await AsyncStorage.setItem(CHAVE_CAIXA, JSON.stringify(vinculado));
  emMemoria = vinculado;
  return vinculado;
}

/** Chamado quando a portaria começa: a partir daí setor só muda com liberação. */
export async function travarSetores(): Promise<void> {
  const caixa = await caixaAberto();
  if (!caixa || caixa.setoresTravados) {
    return;
  }
  const travado: Caixa = {...caixa, setoresTravados: true};
  await AsyncStorage.setItem(CHAVE_CAIXA, JSON.stringify(travado));
  emMemoria = travado;
}

/** Liga ou desliga o mãos livres do caixa aberto. */
export async function definirMaosLivres(ligado: boolean): Promise<void> {
  const caixa = await caixaAberto();
  if (!caixa || Boolean(caixa.maosLivres) === ligado) {
    return;
  }
  const atualizado: Caixa = {...caixa, maosLivres: ligado};
  await AsyncStorage.setItem(CHAVE_CAIXA, JSON.stringify(atualizado));
  emMemoria = atualizado;
}

export async function fecharCaixa(): Promise<void> {
  await AsyncStorage.removeItem(CHAVE_CAIXA);
  emMemoria = null;
}
